import { createAction, props } from '@ngrx/store';

/**
 * Generic slice-persistence actions. Each persisted slice registers a projection selector
 * (see {@link PERSISTED_SLICES}); callers then persist or rehydrate a slice purely by its
 * feature key — the NgRx store stays the source of truth and IndexedDB is written on save
 * and read only to rehydrate at init.
 */

/**
 * Persist the current state of a slice to storage. The persistence effect reads the slice's
 * registered projection selector and writes it under `featureKey`. `correlationId` lets a
 * facade await the specific write it triggered.
 */
export const persistSlice = createAction(
    '[Persistence] Persist slice',
    props<{ featureKey: string; correlationId?: string }>(),
);

/** Dispatched by the persistence effect once a slice's state has been written to storage. */
export const slicePersisted = createAction(
    '[Persistence] Slice persisted',
    props<{ featureKey: string; correlationId?: string }>(),
);

/** Read a slice's persisted state from storage (dispatched from a facade's `init()`). */
export const hydrateSlice = createAction('[Persistence] Hydrate slice', props<{ featureKey: string }>());

/**
 * Dispatched by the persistence effect with a slice's persisted state (or `undefined` when
 * nothing is stored). The owning slice's reducer restores its state from `data`.
 */
export const sliceHydrated = createAction(
    '[Persistence] Slice hydrated',
    props<{ featureKey: string; data: unknown }>(),
);

/** Dispatched by the persistence effect when a persist/hydrate operation fails. */
export const persistenceError = createAction(
    '[Persistence] Error',
    props<{ featureKey: string; error: string; correlationId?: string }>(),
);
